import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { Button, Scrollbar } from '@profgeosoft-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmResetSettingsModal } from 'src/features/confirm-reset-settings-modal';
import { wrap } from 'src/packages/mobx-di/wrap';
import { hasValue } from 'src/packages/utils/has-value';
import {
  HIDDEN_IN_SETTINGS_SIDEBAR_COLUMNS,
  type TableSettingsManager,
} from 'src/services/table-settings-service/table-settings-manager';

import type { DragEndEvent } from '@dnd-kit/core';
import type { GridColDef, GridColumnVisibilityModel } from '@profgeosoft-ui/table';
import type { ReactNode } from 'react';
import type { TControlView } from 'src/services/directory-service/types';

import { DisplaySettingItem } from './display-setting-item';

import styles from './display-settings.module.scss';

type Props = {
  displayManager: TableSettingsManager;
  columns: GridColDef[];
  fieldViews: TControlView[];
  minVisibleColumns?: number;
};

type TColumnViewObject = { view: TControlView; column: GridColDef };

export const DisplaySettings = wrap<Props>(function DisplaySettings({
  fieldViews,
  columns,
  displayManager,
  minVisibleColumns = 1,
}) {
  const { t } = useTranslation();
  const [isResetSettingsModalOpen, setisResetSettingsModalOpen] = useState(false);

  const { leftPinned, free, rightPinned } = useMemo(() => {
    const { left, right } = displayManager.settings.pinnedColumns ?? {};

    const { leftPinned, free, rightPinned } = fieldViews.reduce(
      (
        acc: {
          leftPinned: TColumnViewObject[];
          free: TColumnViewObject[];
          rightPinned: TColumnViewObject[];
        },
        fieldView,
      ) => {
        const column = columns.find((c) => c.field === fieldView.fieldId);

        if (fieldView.hiddenField) {
          return acc;
        }

        if (!column) {
          console.error('cannot find corresponding column', fieldView.fieldId);
          return acc;
        }

        const viewAndColumn = {
          view: fieldView,
          column,
        };

        if (left?.includes(fieldView.fieldId)) {
          acc.leftPinned.push(viewAndColumn);
          return acc;
        }

        if (right?.includes(fieldView.fieldId)) {
          acc.rightPinned.push(viewAndColumn);
          return acc;
        }

        acc.free.push(viewAndColumn);

        return acc;
      },
      {
        leftPinned: [],
        free: [],
        rightPinned: [],
      },
    );

    const columnsOrderModel = displayManager.settings.columnsOrderModel;

    if (columnsOrderModel?.length) {
      free.sort((a, b) => {
        const c1index = columnsOrderModel.indexOf(a.view.fieldId);
        const c2index = columnsOrderModel.indexOf(b.view.fieldId);

        if (c1index < 0 && c2index < 0) {
          return 0;
        }

        if (c1index < 0) {
          return -1;
        }

        if (c2index < 0) {
          return 1;
        }

        return c1index - c2index;
      });
    }

    return { leftPinned, free, rightPinned };
  }, [columns, displayManager.settings.columnsOrderModel, displayManager.settings.pinnedColumns, fieldViews]);

  const handleColumnVisibilityChange = (column: string, value: boolean) => {
    const { settings } = displayManager;

    const newVisibilityModel: GridColumnVisibilityModel = {
      ...settings.columnsVisibility,
      [column]: value,
    };

    displayManager.onColumnVisibilityChange(newVisibilityModel);
  };

  const handleUnpinClick = (field: string) => {
    const { settings } = displayManager;

    if (!settings.pinnedColumns || Object.keys(settings.pinnedColumns).length < 1) return;

    for (const side in settings.pinnedColumns) {
      if (side !== 'left' && side !== 'right') return;

      if (!settings.pinnedColumns[side]?.includes(field)) continue;

      const changedSidePinedModel = settings.pinnedColumns[side]?.filter((column) => column !== field);
      const changedPinModel = { ...settings.pinnedColumns, [side]: changedSidePinedModel };

      displayManager.onPinChange(changedPinModel);
    }
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { settings } = displayManager;

      const savedColumnsOrderModel = settings?.columnsOrderModel ?? [];
      const columnsOrderModel = [...fieldViews.map((v) => v.fieldId)].sort((a, b) => {
        const c1index = savedColumnsOrderModel.indexOf(a);
        const c2index = savedColumnsOrderModel.indexOf(b);

        if (c1index < 0 && c2index < 0) {
          return 0;
        }

        if (c1index < 0) {
          return -1;
        }

        if (c2index < 0) {
          return 1;
        }

        return c1index - c2index;
      });

      const activeField = event.active.id;
      const overField = event.over?.id;

      if (!hasValue(overField) || !hasValue(activeField)) {
        return;
      }

      const newIndex = columnsOrderModel.findIndex((item) => item === overField);
      const oldIndex = columnsOrderModel.findIndex((item) => item === activeField);

      const reorderedColumns = arrayMove(columnsOrderModel, oldIndex, newIndex);

      displayManager.onColumnsOrderModelChange(reorderedColumns);
    },
    [displayManager, fieldViews],
  );

  const checkIsColumnVisibilityDisabled = (columnField: string): boolean => {
    const { settings } = displayManager;

    if (!settings.columnsOrderModel) return false;

    const visibleColumns = settings.columnsVisibility;

    if (!visibleColumns) return false;

    const isColumnVisible = visibleColumns[columnField] === true || visibleColumns[columnField] === undefined;
    let visibleColumnsCount = 0;

    settings.columnsOrderModel.forEach((item) => {
      if (visibleColumns[item] === true || visibleColumns[item] === undefined) {
        visibleColumnsCount += 1;
      }
    });

    const columnDef = columns.find((column) => column.field === columnField);

    if (!columnDef) return true;

    if (columnDef.hideable === false) {
      return true;
    } else {
      if (!isColumnVisible) {
        return false;
      }

      return visibleColumnsCount <= minVisibleColumns;
    }
  };

  const renderItems = (renderedColumns: TColumnViewObject[]): ReactNode => {
    return renderedColumns.map(({ view }) => {
      if (HIDDEN_IN_SETTINGS_SIDEBAR_COLUMNS.includes(view.fieldId)) {
        return null;
      }

      const fieldName = t(`labels:${view.fieldId}.label`);

      return (
        <DisplaySettingItem
          key={view.fieldId}
          field={view.fieldId}
          title={fieldName}
          unpinColumn={handleUnpinClick}
          isVisibilitySwitcherDisabled={checkIsColumnVisibilityDisabled(view.fieldId)}
          isVisible={
            displayManager.settings.columnsVisibility?.[view.fieldId] === undefined
              ? true
              : displayManager.settings.columnsVisibility[view.fieldId]
          }
          isPinned={displayManager.displaySettingPinnedColumns.includes(view.fieldId)}
          onColumnVisibilityChange={handleColumnVisibilityChange}
        />
      );
    });
  };

  return (
    <>
      <Scrollbar>
        {renderItems(leftPinned)}
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
          <SortableContext items={free.map((o) => o.view.fieldId)}>{renderItems(free)}</SortableContext>
        </DndContext>
        {renderItems(rightPinned)}
        <Button className={styles.resetSettingsButton} variant="flat" onClick={() => setisResetSettingsModalOpen(true)}>
          {t('displaySettings:resetSettings')}
        </Button>
      </Scrollbar>
      <ConfirmResetSettingsModal
        isModalOpened={isResetSettingsModalOpen}
        onApply={() => {
          displayManager.resetSettings();
          setisResetSettingsModalOpen(false);
        }}
        onCancel={() => setisResetSettingsModalOpen(false)}
      />
    </>
  );
});
